import { useEffect, useState } from "react";
import { format, parseISO, subDays } from "date-fns";
import { Check, ExternalLink, Pencil, X } from "lucide-react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { YieldApi } from "@/App/api/yield";
import { ConfigApi } from "@/App/api/config";
import { OtaApi } from "@/App/api/ota";
import { getParsedPermissions } from "@/App/auth/auth";
import { hasPermissionYieldSheet } from "@/common/permissionHandler";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/lib/toast";
import type { RootState } from "@/state/store";
import type { HotelMeta } from "@/models/api.models";
import type { IYieldDay } from "@/models/yield.models";

type SuggestedRateRow = {
  date: string;
  currentRate: number;
  owsRate: number;
  suggestedRate: number;
  inventory: number | null;
  pickup: number | null;
  demand: number | null;
  competitorRates: Array<{
    name: string;
    rate: number | string;
    difference: number | null;
  }>;
};

const parseBody = async (
  response: Awaited<ReturnType<typeof YieldApi.getAvailability>>,
) => {
  return (await response.response).body.json();
};

const normalizeDateKey = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [day, month, year] = value.split("-");
    return `${year}-${month}-${day}`;
  }
  const parsed = new Date(Number(value));
  return Number.isNaN(parsed.valueOf()) ? value : format(parsed, "yyyy-MM-dd");
};

const transformCompetitorRate = (value: unknown): number | string => {
  if (typeof value === "number" && value > 0) return value;
  if (typeof value !== "string") return "—";
  const part = value.split(".")[1] || value;
  const los = part.match(/restrictionlos(\d+)/i);
  if (los) return `LOS${los[1]}`;
  const labels: Record<string, string> = {
    nobar: "No BAR",
    soldout: "Sold out",
    meal: "No RO",
    missing: "—",
    onlythirdparty: "3rd party",
    no_person: "No person",
  };
  return labels[part] || part.toUpperCase();
};

const getCompetitorRate = (
  value: unknown,
  rateKey: string,
): number | string => {
  if (!value || typeof value !== "object") return "—";
  const rate = value as Record<string, unknown>;
  const selected = rate[rateKey];
  if (typeof selected === "number" && selected > 0) return selected;
  return transformCompetitorRate(rate[`${rateKey}Message`]);
};

const webClientUrl = import.meta.env.VITE_REACT_APP_WEBCLIENT_URL ?? "";

export function SuggestedRatesPage() {
  const { hotelId: hotelIdParam } = useParams<{ hotelId: string }>();
  const hotelId = hotelIdParam ? Number(hotelIdParam) : null;
  const hotels = useSelector(
    (state: RootState) => state.hotelData.hotels,
  ) as HotelMeta[];

  const [rows, setRows] = useState<SuggestedRateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [rateSourceLabel, setRateSourceLabel] = useState<string | null>(null);
  const [hotelName, setHotelName] = useState<string>("");
  const [canAct, setCanAct] = useState(false);
  const [rateBounds, setRateBounds] = useState<{
    min: number;
    max: number;
  } | null>(null);
  const [editRow, setEditRow] = useState<SuggestedRateRow | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    void getParsedPermissions().then((perm) => {
      if (!cancelled) setCanAct(hasPermissionYieldSheet(perm, hotelId));
    });
    return () => {
      cancelled = true;
    };
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const dates = await YieldApi.getSuggestedDays(hotelId);
        if (dates.length === 0) {
          if (!cancelled) {
            setRows([]);
            setHotelName(
              hotels.find((h) => h.hotelId === hotelId)?.info?.name ||
                hotels.find((h) => h.hotelId === hotelId)?.name ||
                "",
            );
          }
          return;
        }

        const start = dates[0];
        const end = dates[dates.length - 1];
        const [availabilityResponse, hotel] = await Promise.all([
          YieldApi.getAvailability(hotelId, start, end),
          ConfigApi.getHotelConfig(hotelId),
        ]);
        const daysBody = await parseBody(availabilityResponse);
        const days = (Array.isArray(daysBody)
          ? daysBody
          : []) as unknown as IYieldDay[];
        const dayByDate = new Map(days.map((day) => [day.hrDate, day]));
        const otaHotelId = Number(hotel?.ota?.hotelId || 0);
        // Mirrors yieldTable.tsx's parseHotelConfig default: the lookup key is
        // always plain 'RO' or 'BB' (source/1-guest only affect the label,
        // matching what the yield sheet's compset row shows by default).
        const defaultCompsetType = hotel?.ota?.defaultCompsetType;
        const sourceLabel = (hotel?.ota?.defaultSource || "ota").toUpperCase();
        const isBB =
          defaultCompsetType === "BB" || defaultCompsetType === "oneGuestBB";
        const isOneGuest =
          defaultCompsetType === "oneGuestRO" ||
          defaultCompsetType === "oneGuestBB";
        const rateKey = isBB ? "BB" : "RO";
        const label = `[${sourceLabel}] ${isOneGuest ? "1" : "2"} Pax ${rateKey}`;
        // Same min/max bounds as yieldTable.tsx: min and max across all
        // configured room types.
        const bounds = Object.values(hotel?.roomTypes ?? {}).reduce(
          (acc, roomType) => ({
            min: Math.min(acc.min, Number(roomType.minRate || 9999)),
            max: Math.max(acc.max, Number(roomType.maxRate) || 0),
          }),
          { min: 9999, max: 0 },
        );
        if (!cancelled) {
          setRateSourceLabel(label);
          setHotelName(hotel?.info?.name || "");
          setRateBounds(bounds.max > 0 ? bounds : null);
        }

        let demand: Record<string, { demand?: number }> = {};
        let competitorRates: Record<string, unknown> = {};
        let competitorRateCompare: Record<string, Record<string, unknown>> = {};
        if (otaHotelId) {
          const otaPickupDate = format(
            subDays(new Date(), Number(hotel?.meta?.otaPickupRange || 1)),
            "yyyy-MM-dd",
          );
          const [demandResult, ratesResult, compareResult] = await Promise.all([
            OtaApi.getDemand(otaHotelId, start, end),
            OtaApi.getRates(otaHotelId, start, end),
            OtaApi.getPickupCompare(otaHotelId, otaPickupDate, start, end),
          ]);
          demand = demandResult;
          competitorRates = ratesResult;
          competitorRateCompare =
            (
              compareResult as {
                otaRateCompare?: Record<string, Record<string, unknown>>;
              }
            ).otaRateCompare || {};
        }

        let latestInventory: Record<string, number> = {};
        let earlierInventory: Record<string, number | { value?: number }> = {};
        try {
          const now = new Date();
          const pickupDate = subDays(
            now,
            Number(hotel?.meta?.pickupRange || 1),
          );
          const first = await YieldApi.getFirstPickupData(
            hotelId,
            pickupDate.setUTCHours(12, 0, 0, 0),
          );
          const last = await YieldApi.getLastPickupData(
            hotelId,
            now.setUTCHours(12, 0, 0, 0),
          );
          [earlierInventory, latestInventory] = await Promise.all([
            (await first.response).body.json() as Promise<
              Record<string, number | { value?: number }>
            >,
            (await last.response).body.json() as Promise<
              Record<string, number>
            >,
          ]);
        } catch (error) {
          console.error("Failed to load pickup context:", error);
        }

        const nextRows = dates.flatMap((date) => {
          const day = dayByDate.get(date);
          if (!day || day.suggestedBaseRate == null) return [];
          const epochKey = Object.keys(latestInventory).find(
            (key) => normalizeDateKey(key) === date,
          );
          const latest = epochKey ? Number(latestInventory[epochKey]) : NaN;
          const earlierValue = epochKey
            ? earlierInventory[epochKey]
            : undefined;
          const earlier = Number(
            typeof earlierValue === "object"
              ? earlierValue?.value
              : earlierValue,
          );
          const demandEntry = Object.entries(demand).find(
            ([key]) => normalizeDateKey(key) === date,
          )?.[1];
          const competitorDateEntry = Object.entries(competitorRates).find(
            ([key]) => normalizeDateKey(key) === date,
          );
          const competitorEntry = competitorDateEntry?.[1] as
            Record<string, unknown> | undefined;
          const competitorCompareEntry = competitorDateEntry
            ? competitorRateCompare[competitorDateEntry[0]] ||
              Object.entries(competitorRateCompare).find(
                ([key]) => normalizeDateKey(key) === date,
              )?.[1]
            : undefined;

          return [
            {
              date,
              currentRate: Number(day.actualBaseRate),
              owsRate: Number(day.actualOWSBaseRate),
              suggestedRate: Number(day.suggestedBaseRate),
              inventory: Number.isFinite(latest)
                ? latest
                : (day.availability?.leftToSell ?? null),
              pickup:
                Number.isFinite(earlier) && Number.isFinite(latest)
                  ? earlier - latest
                  : null,
              demand:
                typeof demandEntry?.demand === "number"
                  ? Math.round(demandEntry.demand * 100)
                  : null,
              competitorRates: Object.entries(competitorEntry || {}).map(
                ([name, rate]) => {
                  const currentRate = getCompetitorRate(rate, rateKey);
                  const previousRate = getCompetitorRate(
                    competitorCompareEntry?.[name],
                    rateKey,
                  );
                  return {
                    name,
                    rate: currentRate,
                    difference:
                      typeof currentRate === "number" &&
                      typeof previousRate === "number"
                        ? Math.ceil(currentRate - previousRate)
                        : null,
                  };
                },
              ),
            },
          ];
        });

        if (!cancelled) {
          setRows(nextRows);
          setSelectedDate((current) =>
            nextRows.some((row) => row.date === current)
              ? current
              : (nextRows[0]?.date ?? null),
          );
        }
      } catch (error) {
        console.error("Failed to load suggested rates:", error);
        toast.error("Failed to load suggested rates.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const selectedRow =
    rows.find((row) => row.date === selectedDate) || rows[0] || null;

  const openInWebApp = () => {
    if (!hotelId || !webClientUrl) return;
    window.open(`${webClientUrl}/${hotelId}`, "_blank", "noopener,noreferrer");
  };

  const decide = async (row: SuggestedRateRow, accept: boolean) => {
    if (!hotelId) return;
    setProcessing(row.date);
    try {
      const payload = { hotelId, start: row.date, end: row.date };
      const request = accept
        ? YieldApi.acceptSuggestedRate(payload)
        : YieldApi.removeSuggestedRate(payload);
      const response = await (await request).response;
      if (response.statusCode !== 200) throw new Error("Decision failed");
      if (accept) {
        // Publish only the accepted day
        const publishResponse = await (
          await YieldApi.publishRatesInView(payload)
        ).response;
        if (publishResponse.statusCode >= 300)
          throw new Error("Publish failed");
      }
      const remaining = rows.filter((item) => item.date !== row.date);
      setRows(remaining);
      if (selectedDate === row.date) {
        setSelectedDate(remaining[0]?.date ?? null);
      }
      toast.success(
        accept ? "Suggestion accepted and published" : "Suggestion rejected",
      );
    } catch (error) {
      console.error("Failed to review suggested rate:", error);
      toast.error("Failed to update the suggested rate.");
    } finally {
      setProcessing(null);
    }
  };

  const startEdit = (row: SuggestedRateRow) => {
    setEditValue(String(row.suggestedRate));
    setEditRow(row);
  };

  const saveEdit = async () => {
    if (!hotelId || !editRow) return;
    const value = editValue.trim();
    const parsed = Number.parseFloat(value);
    if (value === "" || !Number.isFinite(parsed)) {
      toast.error("Enter a valid rate.");
      return;
    }
    if (parsed === editRow.suggestedRate || parsed === editRow.currentRate) {
      setEditRow(null);
      return;
    }
    if (rateBounds && (parsed < rateBounds.min || parsed > rateBounds.max)) {
      toast.error(
        `Rate must be between ${rateBounds.min} and ${rateBounds.max}.`,
      );
      return;
    }
    setSavingEdit(true);
    try {
      const response = await (
        await YieldApi.updateSuggestedBaseRate({
          hotelId,
          start: editRow.date,
          end: editRow.date,
          suggestedRate: parsed,
        })
      ).response;
      if (response.statusCode !== 200) throw new Error("Update failed");
      setRows((current) =>
        current.map((item) =>
          item.date === editRow.date
            ? { ...item, suggestedRate: parsed }
            : item,
        ),
      );
      setEditRow(null);
      toast.success("Suggested rate updated");
    } catch (error) {
      console.error("Failed to update suggested rate:", error);
      toast.error("Failed to update the suggested rate.");
    } finally {
      setSavingEdit(false);
    }
  };

  if (!hotelId) return null;

  if (
    hotels.length > 0 &&
    !hotels.some((hotel) => Number(hotel.hotelId) === hotelId)
  ) {
    return (
      <div className="rounded-md border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        You do not have access to this hotel.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold">
            Suggested rates{hotelName ? ` · ${hotelName}` : ""}
          </h1>
          <p className="text-xs text-muted-foreground">
            {rows.length} pending day{rows.length === 1 ? "" : "s"}
          </p>
        </div>
        {webClientUrl && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={openInWebApp}
          >
            <ExternalLink className="h-4 w-4" />
            <span className="hidden sm:inline">Open in web app</span>
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center gap-2 text-sm text-muted-foreground">
          <Spinner className="h-5 w-5" /> Loading decision context...
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          All suggested rates have been reviewed.
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2 md:hidden">
            {rows.map((row) => {
              const difference = row.suggestedRate - row.currentRate;
              const selected = selectedRow?.date === row.date;
              const stats: Array<{
                label: string;
                value: React.ReactNode;
                emphasize?: boolean;
              }> = [
                { label: "Base", value: row.currentRate },
                { label: "OWS base", value: row.owsRate || "—" },
                {
                  label: "Suggested",
                  emphasize: true,
                  value: (
                    <span className="inline-flex items-center justify-center gap-1">
                      {canAct ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
                          onClick={(event) => {
                            event.stopPropagation();
                            startEdit(row);
                          }}
                          aria-label={`Edit suggested rate for ${row.date}`}
                        >
                          {row.suggestedRate}
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </button>
                      ) : (
                        row.suggestedRate
                      )}
                      <span
                        className={`rounded px-1 py-0.5 text-[10px] font-bold ${
                          difference > 0
                            ? "bg-green-100 text-green-700"
                            : difference < 0
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {difference > 0 ? "+" : ""}
                        {difference}
                      </span>
                    </span>
                  ),
                },
                { label: "Left to sell", value: row.inventory ?? "—" },
                { label: "Pickup", value: row.pickup ?? "—" },
                {
                  label: "Demand",
                  value: row.demand !== null ? `${row.demand}%` : "—",
                },
              ];
              return (
                <div
                  key={row.date}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedDate(row.date)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      setSelectedDate(row.date);
                    }
                  }}
                  className={`rounded-md border p-3 text-left ${
                    selected
                      ? "border-blue-300 bg-blue-50"
                      : "bg-white hover:bg-muted/30"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {format(parseISO(row.date), "EEE dd MMM yyyy")}
                    </span>
                    {canAct && (
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 text-red-700"
                          disabled={processing === row.date}
                          onClick={(event) => {
                            event.stopPropagation();
                            void decide(row, false);
                          }}
                          aria-label={`Reject suggestion for ${row.date}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          className="h-8 w-8 bg-green-700 hover:bg-green-800"
                          disabled={processing === row.date}
                          onClick={(event) => {
                            event.stopPropagation();
                            void decide(row, true);
                          }}
                          aria-label={`Accept suggestion for ${row.date}`}
                        >
                          {processing === row.date ? (
                            <Spinner className="h-4 w-4" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-2 text-center">
                    {stats.map((stat) => (
                      <div key={stat.label}>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {stat.label}
                        </div>
                        <div
                          className={`text-sm ${stat.emphasize ? "font-semibold" : "font-medium"}`}
                        >
                          {stat.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden overflow-x-auto rounded-md border md:block">
            <table className="w-full min-w-[720px] table-fixed text-sm">
              <thead className="bg-muted text-xs text-muted-foreground">
                <tr>
                  <th className="w-28 py-2 pl-2 pr-1 text-left">
                    Date of arrival
                  </th>
                  <th className="w-14 px-1 py-2 text-center">Base</th>
                  <th className="w-16 px-1 py-2 text-center">OWS base</th>
                  <th className="w-20 px-1 py-2 text-center">Suggested</th>
                  <th className="w-16 px-1 py-2 text-center">Left to Sell</th>
                  <th className="w-14 px-1 py-2 text-center">Pickup</th>
                  <th className="w-16 px-1 py-2 text-center">Demand</th>
                  {canAct && (
                    <th className="w-24 py-2 pl-1 pr-2 text-center">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const difference = row.suggestedRate - row.currentRate;
                  const selected = selectedRow?.date === row.date;
                  return (
                    <tr
                      key={row.date}
                      className={`cursor-pointer border-t ${selected ? "bg-blue-50" : "hover:bg-muted/30"}`}
                      onClick={() => setSelectedDate(row.date)}
                    >
                      <td className="py-3 pl-2 pr-1 font-medium">
                        {format(parseISO(row.date), "dd MMM yyyy")}
                      </td>
                      <td className="px-1 py-3 text-center">
                        {row.currentRate}
                      </td>
                      <td className="px-1 py-3 text-center">
                        {row.owsRate || "—"}
                      </td>
                      <td className="px-1 py-3 text-center font-semibold">
                        <div className="flex items-center justify-center gap-1">
                          {canAct ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
                              onClick={(event) => {
                                event.stopPropagation();
                                startEdit(row);
                              }}
                              aria-label={`Edit suggested rate for ${row.date}`}
                            >
                              {row.suggestedRate}
                              <Pencil className="h-3 w-3 text-muted-foreground" />
                            </button>
                          ) : (
                            <span>{row.suggestedRate}</span>
                          )}
                          <span
                            className={`rounded px-1 py-0.5 text-[10px] font-bold ${
                              difference > 0
                                ? "bg-green-100 text-green-700"
                                : difference < 0
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {difference > 0 ? "+" : ""}
                            {difference}
                          </span>
                        </div>
                      </td>
                      <td className="px-1 py-3 text-center">
                        {row.inventory ?? "—"}
                      </td>
                      <td className="px-1 py-3 text-center">
                        {row.pickup ?? "—"}
                      </td>
                      <td className="px-1 py-3 text-center">
                        {row.demand !== null ? `${row.demand}%` : "—"}
                      </td>
                      {canAct && (
                        <td className="py-3 pl-1 pr-2">
                          <div className="flex justify-center gap-1">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 text-red-700"
                              disabled={processing === row.date}
                              onClick={(event) => {
                                event.stopPropagation();
                                void decide(row, false);
                              }}
                              aria-label={`Reject suggestion for ${row.date}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              className="h-8 w-8 bg-green-700 hover:bg-green-800"
                              disabled={processing === row.date}
                              onClick={(event) => {
                                event.stopPropagation();
                                void decide(row, true);
                              }}
                              aria-label={`Accept suggestion for ${row.date}`}
                            >
                              {processing === row.date ? (
                                <Spinner className="h-4 w-4" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <aside className="rounded-md border bg-muted/20 p-3">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-2">
                <h3 className="text-sm font-semibold">Competitor rates</h3>
                {rateSourceLabel && (
                  <span
                    className="rounded border bg-background px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground"
                    title="Default competitive set source, matches the yield sheet's compset row"
                  >
                    {rateSourceLabel}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedRow
                  ? format(parseISO(selectedRow.date), "dd MMM yyyy")
                  : "Select a date"}
              </p>
            </div>
            {selectedRow?.competitorRates.length ? (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {selectedRow.competitorRates.map((competitor) => (
                  <div
                    key={competitor.name}
                    className="rounded-md border bg-background p-2.5"
                  >
                    <div
                      className="truncate text-xs text-muted-foreground"
                      title={competitor.name}
                    >
                      {competitor.name}
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-base font-semibold">
                        {competitor.rate}
                      </span>
                      {competitor.difference !== null && (
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs font-bold ${
                            competitor.difference > 0
                              ? "bg-green-100 text-green-700"
                              : competitor.difference < 0
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-600"
                          }`}
                          title="Change from OTA pickup snapshot"
                        >
                          {competitor.difference > 0 ? "+" : ""}
                          {competitor.difference}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No competitor rates available.
              </p>
            )}
          </aside>
        </>
      )}
      <Dialog
        open={editRow !== null}
        onOpenChange={(open) => {
          if (!open && !savingEdit) setEditRow(null);
        }}
      >
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Edit suggested rate</DialogTitle>
            <DialogDescription>
              {editRow
                ? `${format(parseISO(editRow.date), "EEE dd MMM yyyy")} · current base rate ${editRow.currentRate}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void saveEdit();
            }}
          >
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              min={rateBounds?.min}
              max={rateBounds?.max}
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
              disabled={savingEdit}
              autoFocus
              aria-label="Suggested rate"
            />
            {rateBounds && (
              <p className="text-xs text-muted-foreground">
                Allowed range: {rateBounds.min} - {rateBounds.max}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={savingEdit}
                onClick={() => setEditRow(null)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={savingEdit}>
                {savingEdit ? <Spinner className="h-4 w-4" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SuggestedRatesPage;
